import { ScheduleHelp } from '../genericHelp/schedule.help';
import { ParameterFormatService } from '../services/parameter-format.service';

type TelemetryParameter = {
  id?: string;
  name?: string;
  categoryId?: string;
  subcategory?: string;
  orderInSubcategory?: number;
  userAccess?: string;
  value?: string;
  enumCoding?: Record<string, string>;
  unit?: string;
  factor?: number;
};

type TelemetryCategory = {
  id?: string;
  name?: string;
  order?: number;
  subcategories?: string[];
};

type TelemetryParameterData = {
  parameterId?: string;
  data?: string;
  isUserData?: boolean;
};

type TelemetryExportParameter = {
  id: string;
  name: string;
  value?: string;
};

type TelemetryExportSubcategory = {
  name: string;
  parameters?: TelemetryExportParameter[];
  data?: unknown;
};

type TelemetryExportCategory = {
  name: string;
  subcategories: TelemetryExportSubcategory[];
};

type TelemetryCustomCategory = {
  name: string;
  subcategories: TelemetryExportSubcategory[];
};

type TelemetryExportMetadataInput = {
  deviceLabel: string;
  exportSourceLabel: string;
};

type BuildDesktopVersionInput = {
  frontendVersion?: string;
  desktopBackendVersion?: string;
};

type TelemetryScheduleLike = {
  scheduleType?: string;
  userDefined?: unknown;
  [key: string]: unknown;
};

type ExportTelemetryJsonInput = {
  parameters?: TelemetryParameter[];
  categories?: TelemetryCategory[];
  parameterDataList?: TelemetryParameterData[];
  userAccess?: string[];
  metadata?: Record<string, unknown>;
  includeExportedAt?: boolean;
  customCategories?: TelemetryCustomCategory[];
  filteredSchedules?: TelemetryScheduleLike[];
  virtualFenceConstructionRules?: unknown[];
};

export function exportTelemetryJson({
  parameters = [],
  categories = [],
  parameterDataList = [],
  userAccess = [],
  metadata = {},
  includeExportedAt = true,
  customCategories = [],
  filteredSchedules = [],
  virtualFenceConstructionRules = [],
}: ExportTelemetryJsonInput) {
  const parameterFormatService = new ParameterFormatService();
  const parameterDataByParameterId = new Map<
    string,
    { factory?: TelemetryParameterData; user?: TelemetryParameterData }
  >();

  parameterDataList.forEach((parameterData) => {
    if (!parameterData?.parameterId) return;
    const current = parameterDataByParameterId.get(parameterData.parameterId) ?? {};
    if (parameterData.isUserData) current.user = parameterData;
    else current.factory = parameterData;
    parameterDataByParameterId.set(parameterData.parameterId, current);
  });

  const sortedCategories = [...categories].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const exportCategories: TelemetryExportCategory[] = [];

  sortedCategories.forEach((category) => {
    if (!category?.id || !category?.name) return;

    const subcategoryNames = category.subcategories ? [...category.subcategories] : [];
    subcategoryNames.push(undefined);

    const exportSubcategories: TelemetryExportSubcategory[] = [];

    subcategoryNames.forEach((subcategoryName) => {
      const exportParameters: TelemetryExportParameter[] = [];
      parameters
        .filter(
          (parameter) =>
            Boolean(parameter?.id) &&
            parameter.categoryId === category.id &&
            parameter.subcategory === subcategoryName &&
            (userAccess.length === 0 || userAccess.includes(parameter.userAccess)),
        )
        .sort((a, b) => (a.orderInSubcategory ?? 0) - (b.orderInSubcategory ?? 0))
        .forEach((parameter) => {
          const { factory, user } = parameterDataByParameterId.get(parameter.id!) ?? {};
          if (!factory && !user) return;
          const value =
            parameterFormatService.getFormattedData(user as any, parameter as any) ||
            parameterFormatService.getFormattedData(factory as any, parameter as any);
          exportParameters.push({
            id: parameter.id!,
            name: parameter.name ?? parameter.id!,
            value,
          });
        });

      if (!exportParameters.length) return;
      exportSubcategories.push({
        name: subcategoryName ?? 'No Subcategory',
        parameters: exportParameters,
      });
    });

    if (!exportSubcategories.length) return;
    exportCategories.push({
      name: category.name,
      subcategories: exportSubcategories,
    });
  });

  const payload: Record<string, unknown> = {
    ...metadata,
    categories: [
      ...exportCategories,
      ...customCategories,
      ...buildTelemetryCustomCategories({ filteredSchedules, virtualFenceConstructionRules }),
    ],
  };
  if (includeExportedAt) payload['exportedAt'] = new Date().toISOString();
  return payload;
}

export function buildTelemetryExportMetadata({
  deviceLabel,
  exportSourceLabel,
}: TelemetryExportMetadataInput) {
  return {
    device: deviceLabel,
    'created at': new Date(),
    'exported from': exportSourceLabel,
  };
}

function buildTelemetryCustomCategories({
  filteredSchedules = [],
  virtualFenceConstructionRules = [],
}: {
  filteredSchedules?: TelemetryScheduleLike[];
  virtualFenceConstructionRules?: unknown[];
}) {
  return [
    ...(filteredSchedules.length
      ? [
          {
            name: 'Schedules',
            subcategories: filteredSchedules.map((schedule) => {
              const { userDefined, ...exportSchedule } = schedule;
              return {
                name:
                  ScheduleHelp.toLowerCase(schedule.scheduleType as never) ?? 'Unknown Schedule',
                data: exportSchedule,
              };
            }),
          },
        ]
      : []),
    ...(virtualFenceConstructionRules.length
      ? [
          {
            name: 'Virtual Fence Map',
            subcategories: [
              {
                name: 'Rules',
                data: virtualFenceConstructionRules,
              },
            ],
          },
        ]
      : []),
  ];
}

export function buildTelemetryExportFilePrefix(deviceClassName: string, deviceId: number | string) {
  const normalizedDeviceClassName = (deviceClassName || 'Device').replace(/[^a-zA-Z0-9]+/g, '');
  return `Telemetry-${normalizedDeviceClassName}${deviceId}`;
}

export function downloadJsonFile(filePrefix: string, payload: unknown) {
  const timestamp = new Date()
    .toISOString()
    .replace(/\.\d{3}Z$/, 'Z')
    .replace(/:/g, '-');
  const fileName = `${filePrefix}-${timestamp}.json`;
  const fileData = JSON.stringify(payload, null, 2);
  const blob = new Blob([fileData], { type: 'application/json;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

export function buildDesktopVersion({
  frontendVersion,
  desktopBackendVersion,
}: BuildDesktopVersionInput) {
  const v1 = frontendVersion?.split('.').map((v) => +v);
  const v2 = desktopBackendVersion?.split('.').map((v) => +v);
  if (!v1 || !v2 || v1.length < 3 || v2.length < 3) return frontendVersion;
  return [v1[0] + v2[0], v1[1] + v2[1], v1[2] + v2[2]].join('.');
}
